import React from 'react';
import { 
  Globe, Link2, Zap, TreePine, Coffee, ShoppingBag, Disc, Heart, Video, 
  Users, Music, Palette, Camera, MessageCircle, Tv, Package, 
  Briefcase, Headphones, Gamepad2, MessageSquare,
  Newspaper, ShoppingCart, BookOpen, Star, CreditCard, DollarSign,
} from 'lucide-react';

// Temporary replacement for icon-mapping.service.tsx
// Uses only Lucide icons to avoid react-icons dependency

export interface IconMappingResult {
  icon: React.ReactNode;
  color: string;
  source: 'exact' | 'fuzzy' | 'category' | 'fallback';
  confidence: number;
}

export class IconMappingService {
  private readonly DEFAULT_COLOR = '#6B7280';
  private readonly iconClass = "w-5 h-5";

  getIcon(platformId?: string, platformName?: string, aiSuggestedIcon?: string, color?: string): IconMappingResult {
    const iconColor = color || this.DEFAULT_COLOR;
    const icon = this.getExactIconMatch(platformId?.toLowerCase() || '', iconColor) || 
                 <Globe className={this.iconClass} style={{ color: iconColor }} />;
    
    return {
      icon,
      color: iconColor,
      source: 'exact',
      confidence: 1.0
    };
  }

  private getExactIconMatch(platformId: string, color: string): React.ReactNode | null {
    // Using Lucide icons as placeholders for all platforms
    // TODO: Replace with actual brand SVGs or icon fonts
    const iconMap: { [key: string]: React.ReactNode } = {
      // Link-in-bio
      'linktree': <TreePine className={this.iconClass} style={{ color }} />,
      'beacons': <Zap className={this.iconClass} style={{ color }} />,
      'biolink': <Link2 className={this.iconClass} style={{ color }} />,
      'slink': <Link2 className={this.iconClass} style={{ color }} />,
      'linkme': <Link2 className={this.iconClass} style={{ color }} />,
      
      // Social Media
      'twitter': <MessageCircle className={this.iconClass} style={{ color }} />,
      'x': <MessageCircle className={this.iconClass} style={{ color }} />,
      'instagram': <Camera className={this.iconClass} style={{ color }} />,
      'facebook': <Users className={this.iconClass} style={{ color }} />,
      'tiktok': <Video className={this.iconClass} style={{ color }} />,
      'snapchat': <Camera className={this.iconClass} style={{ color }} />,
      'discord': <MessageSquare className={this.iconClass} style={{ color }} />,
      'reddit': <MessageSquare className={this.iconClass} style={{ color }} />,
      'pinterest': <Package className={this.iconClass} style={{ color }} />,
      'tumblr': <BookOpen className={this.iconClass} style={{ color }} />,
      
      // Messaging
      'whatsapp': <MessageSquare className={this.iconClass} style={{ color }} />,
      'telegram': <MessageSquare className={this.iconClass} style={{ color }} />,
      'signal': <MessageSquare className={this.iconClass} style={{ color }} />,
      'slack': <MessageSquare className={this.iconClass} style={{ color }} />,
      
      // Video Platforms
      'youtube': <Video className={this.iconClass} style={{ color }} />,
      'twitch': <Tv className={this.iconClass} style={{ color }} />,
      'vimeo': <Video className={this.iconClass} style={{ color }} />,
      'netflix': <Tv className={this.iconClass} style={{ color }} />,
      
      // Creator Platforms
      'patreon': <Heart className={this.iconClass} style={{ color }} />,
      'onlyfans': <Star className={this.iconClass} style={{ color }} />,
      'kofi': <Coffee className={this.iconClass} style={{ color }} />,
      'substack': <Newspaper className={this.iconClass} style={{ color }} />,
      'buymeacoffee': <Coffee className={this.iconClass} style={{ color }} />,
      
      // Music Platforms
      'spotify': <Music className={this.iconClass} style={{ color }} />,
      'soundcloud': <Headphones className={this.iconClass} style={{ color }} />,
      'bandcamp': <Disc className={this.iconClass} style={{ color }} />,
      'applemusic': <Music className={this.iconClass} style={{ color }} />,
      
      // Professional
      'linkedin': <Briefcase className={this.iconClass} style={{ color }} />,
      'github': <Globe className={this.iconClass} style={{ color }} />,
      'behance': <Palette className={this.iconClass} style={{ color }} />,
      'dribbble': <Palette className={this.iconClass} style={{ color }} />,
      
      // Gaming
      'steam': <Gamepad2 className={this.iconClass} style={{ color }} />,
      'playstation': <Gamepad2 className={this.iconClass} style={{ color }} />,
      'xbox': <Gamepad2 className={this.iconClass} style={{ color }} />,
      'nintendo': <Gamepad2 className={this.iconClass} style={{ color }} />,
      
      // E-commerce
      'etsy': <ShoppingBag className={this.iconClass} style={{ color }} />,
      'amazon': <ShoppingCart className={this.iconClass} style={{ color }} />,
      'shopify': <ShoppingBag className={this.iconClass} style={{ color }} />,
      
      // Financial
      'paypal': <CreditCard className={this.iconClass} style={{ color }} />,
      'venmo': <DollarSign className={this.iconClass} style={{ color }} />,
      'cashapp': <DollarSign className={this.iconClass} style={{ color }} />,
      
      // Default
      'default': <Globe className={this.iconClass} style={{ color }} />,
    };

    return iconMap[platformId] || iconMap['default'];
  }

  getFuzzyIconMatch(platformName: string, color: string): IconMappingResult | null {
    return {
      icon: <Globe className={this.iconClass} style={{ color }} />,
      color,
      source: 'fuzzy',
      confidence: 0.5
    };
  }

  getCategoryIcon(category: string, color: string): IconMappingResult {
    return {
      icon: <Globe className={this.iconClass} style={{ color }} />,
      color,
      source: 'category',
      confidence: 0.3
    };
  }
}

export const iconMappingService = new IconMappingService();